/**
 * Analytical Stats Summary Dashboard
 * Displays high-level clinical metrics: Period Average BP with AHA Stamp,
 * Fluctuation Stability, Day/Night Variations, and AHA Category Distribution Meter.
 */

import React, { useMemo } from 'react';
import { BPStatistics, BloodPressureRecord, BPCategory } from '@/types/record';
import { classifyBP, BP_CATEGORIES_METADATA } from '@/utils/bpClassification';
import { getFluctuationEvaluation, getPulsePressureEvaluation, calculateFluctuation } from '@/utils/calculations';
import { StampBadge } from '@/components/common/StampBadge';

export interface StatsSummaryProps {
  stats: BPStatistics;
  records: BloodPressureRecord[];
  className?: string;
}

export const StatsSummary: React.FC<StatsSummaryProps> = ({
  stats,
  records,
  className = '',
}) => {
  // Overall category evaluation for average BP
  const avgCategory = useMemo(() => {
    if (stats.totalRecords === 0) return null;
    return classifyBP(stats.overallAvgSystolic, stats.overallAvgDiastolic);
  }, [stats.overallAvgSystolic, stats.overallAvgDiastolic, stats.totalRecords]);

  // Pulse pressure assessment
  const ppEval = useMemo(() => {
    return getPulsePressureEvaluation(stats.overallPulsePressure);
  }, [stats.overallPulsePressure]);

  // Fluctuation assessment across active record set
  const fluctuationData = useMemo(() => {
    return calculateFluctuation(records);
  }, [records]);

  const flucEval = useMemo(() => {
    return getFluctuationEvaluation(fluctuationData.systolicFluctuation);
  }, [fluctuationData.systolicFluctuation]);

  // AHA Category distribution percentages
  const distribution = useMemo(() => {
    const total = stats.totalRecords || 1;
    const cats: BPCategory[] = ['NORMAL', 'ELEVATED', 'STAGE_1', 'STAGE_2', 'CRISIS'];
    return cats.map((cat) => {
      const count = stats.categoryCounts[cat] || 0;
      const percent = Math.round((count / total) * 100);
      const meta = BP_CATEGORIES_METADATA[cat];
      return {
        category: cat,
        count,
        percent,
        meta,
      };
    });
  }, [stats.categoryCounts, stats.totalRecords]);

  if (stats.totalRecords === 0) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 4 Key Analytic Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Overall Period Average BP */}
        <div className="bg-paper-100 border border-paper-400 rounded-xs shadow-paper p-4 relative overflow-hidden">
          <div className="flex items-center justify-between text-[11px] font-serif uppercase tracking-wider text-ink-500 pb-1.5 border-b border-paper-300">
            <span>期間平均血壓 (MEAN BP)</span>
            <span>{stats.totalRecords} 筆量測</span>
          </div>

          <div className="my-2.5 flex items-baseline gap-1.5">
            <span className="font-mono text-3xl font-black text-ink-950 tracking-tight">
              {stats.overallAvgSystolic}
            </span>
            <span className="text-xl font-mono text-ink-400">/</span>
            <span className="font-mono text-2xl font-bold text-ink-800">
              {stats.overallAvgDiastolic}
            </span>
            <span className="font-serif text-xs text-ink-500 ml-1">mmHg</span>
          </div>

          <div className="flex items-center justify-between pt-1">
            {avgCategory && (
              <StampBadge category={avgCategory.category} size="sm" />
            )}
            <div className="text-[11px] font-mono text-ink-600 bg-white px-2 py-0.5 border border-paper-300 rounded-xs">
              脈搏: <strong>{stats.overallAvgPulse}</strong> bpm
            </div>
          </div>
        </div>

        {/* Card 2: Derived Physiological Metrics (MAP & PP) */}
        <div className="bg-paper-100 border border-paper-400 rounded-xs shadow-paper p-4 relative overflow-hidden">
          <div className="flex items-center justify-between text-[11px] font-serif uppercase tracking-wider text-ink-500 pb-1.5 border-b border-paper-300">
            <span>動脈壓與血管彈性 (MAP / PP)</span>
            <span>CLINICAL</span>
          </div>

          <div className="my-2 grid grid-cols-2 gap-2">
            <div>
              <span className="text-[11px] font-serif text-ink-600 block">平均動脈壓 (MAP)</span>
              <span className="font-mono text-xl font-bold text-ink-900">
                {stats.overallMAP} <span className="text-[10px] text-ink-500 font-serif">mmHg</span>
              </span>
            </div>
            <div>
              <span className="text-[11px] font-serif text-ink-600 block">脈壓差 (PP)</span>
              <span className="font-mono text-xl font-bold text-ink-900">
                {stats.overallPulsePressure} <span className="text-[10px] text-ink-500 font-serif">mmHg</span>
              </span>
            </div>
          </div>

          <div className="pt-1 text-[11px] font-serif text-ink-700 truncate">
            評估：<strong>{ppEval.labelZh}</strong> (30~50 正常)
          </div>
        </div>

        {/* Card 3: Blood Pressure Fluctuation & Extreme Bounds */}
        <div className="bg-paper-100 border border-paper-400 rounded-xs shadow-paper p-4 relative overflow-hidden">
          <div className="flex items-center justify-between text-[11px] font-serif uppercase tracking-wider text-ink-500 pb-1.5 border-b border-paper-300">
            <span>極值與波動範圍 (EXTREMES)</span>
            <span className={`px-1.5 py-0.2 text-[10px] font-serif font-bold rounded-xs border ${flucEval.colorClass}`}>
              {flucEval.labelZh}
            </span>
          </div>

          <div className="my-2 space-y-1 font-mono text-xs text-ink-800">
            <div className="flex justify-between">
              <span className="text-ink-600 font-serif">收縮壓範圍:</span>
              <span><strong>{fluctuationData.minSystolic}</strong> ~ <strong>{fluctuationData.maxSystolic}</strong> (Δ {fluctuationData.systolicFluctuation})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-600 font-serif">舒張壓範圍:</span>
              <span><strong>{fluctuationData.minDiastolic}</strong> ~ <strong>{fluctuationData.maxDiastolic}</strong> (Δ {fluctuationData.diastolicFluctuation})</span>
            </div>
          </div>

          <div className="pt-1 text-[10px] font-serif text-ink-500 truncate">
            {flucEval.description}
          </div>
        </div>

        {/* Card 4: Morning vs Evening Circadian Comparison & Target Attainment */}
        <div className="bg-paper-100 border border-paper-400 rounded-xs shadow-paper p-4 relative overflow-hidden">
          <div className="flex items-center justify-between text-[11px] font-serif uppercase tracking-wider text-ink-500 pb-1.5 border-b border-paper-300">
            <span>日夜與達標率 (CIRCADIAN & TIR)</span>
            {stats.targetAttainmentRate !== undefined && (
              <span className="font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.2 rounded-xs border border-emerald-300">
                達標 {stats.targetAttainmentRate}%
              </span>
            )}
          </div>

          <div className="my-2 space-y-1 text-xs">
            <div className="flex items-center justify-between font-mono">
              <span className="font-serif text-ink-700 flex items-center gap-1">
                <span>🌅 晨間均值:</span>
              </span>
              <span>
                {stats.morningAvgSystolic !== undefined
                  ? <strong>{stats.morningAvgSystolic} / {stats.morningAvgDiastolic}</strong>
                  : <span className="text-ink-400">無紀錄</span>}
              </span>
            </div>

            <div className="flex items-center justify-between font-mono">
              <span className="font-serif text-ink-700 flex items-center gap-1">
                <span>🌙 晚間均值:</span>
              </span>
              <span>
                {stats.eveningAvgSystolic !== undefined
                  ? <strong>{stats.eveningAvgSystolic} / {stats.eveningAvgDiastolic}</strong>
                  : <span className="text-ink-400">無紀錄</span>}
              </span>
            </div>
          </div>

          <div className="pt-1 text-[10px] font-serif text-ink-600 border-t border-paper-200">
            🎯 理想/正常血壓控制達標率：<strong>{stats.targetAttainmentRate || 0}%</strong>
          </div>
        </div>
      </div>

      {/* AHA Category Distribution Progress Bar */}
      <div className="bg-paper-100 border border-paper-400 rounded-xs shadow-paper p-3.5 sm:p-4">
        <div className="flex items-center justify-between text-xs font-serif mb-2">
          <span className="font-bold text-ink-900 uppercase tracking-wider">
            AHA 臨床血壓等級分佈比例 (Category Distribution)
          </span>
          <span className="text-ink-500 font-mono text-[11px]">
            共 {stats.totalRecords} 次量測
          </span>
        </div>

        {/* Multi-segment Meter Bar */}
        <div className="w-full h-3 bg-paper-300 rounded-xs overflow-hidden flex border border-paper-400">
          {distribution.map((d) => {
            if (d.count === 0) return null;
            return (
              <div
                key={d.category}
                style={{
                  width: `${d.percent}%`,
                  backgroundColor: d.meta.chartColor,
                }}
                title={`${d.meta.labelZh}: ${d.count} 次 (${d.percent}%)`}
                className="h-full transition-all duration-300 relative group"
              />
            );
          })}
        </div>

        {/* Category Legend & Counts */}
        <div className="flex flex-wrap items-center justify-between gap-2 mt-2.5 pt-2 border-t border-paper-300 text-xs font-serif">
          {distribution.map((d) => (
            <div key={d.category} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-xs inline-block"
                style={{ backgroundColor: d.meta.chartColor }}
              />
              <span className="text-ink-800">{d.meta.labelZh}:</span>
              <strong className="font-mono text-ink-950">{d.count} 次</strong>
              <span className="text-ink-400 text-[11px] font-mono">({d.percent}%)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
